"use client"

import React, { useState } from "react"
import Modal from "../../../../shared/components/Modal"
import MODAL_NAMES from "../../../dialogs/ModalName"
import showModalFromId from "../../../../shared/functions/ModalShow"
import { useAppContext } from "../../../../shared/functions/Context"
import type { ITemplates } from "../../../../shared/models/three-sixty-feedback-models/Templates"
import { defaultTemplate } from "../../../../shared/models/three-sixty-feedback-models/Templates"
import EditTemplateModal from "../modals/EditTemplateModal"

import { Edit, Trash2, MessageSquare, Play, Square, RotateCcw, Info, FileText, AlertTriangle, CheckCircle2, Heart, Crown } from 'lucide-react'

// Assuming these types are available from your shared models or defined globally
interface IStatement {
  statement: string
}
interface IValue {
  valueName: string
  statements: IStatement[]
}
interface ILeadership {
  criteriaName: string
  statements: IStatement[]
}

// Helper functions for consistent styling (copied from UserQuestionnaireBox logic)
function isValue(item: IValue | ILeadership): item is IValue {
  return "valueName" in item
}

const getRatingColor = (index: number) => {
  const colors = [
    "from-red-500 to-red-600", // Very Bad
    "from-orange-500 to-orange-600", // Bad
    "from-yellow-500 to-yellow-600", // Better
    "from-blue-500 to-blue-600", // Good
    "from-green-500 to-green-600", // Excellent
  ]
  return colors[index]
}

const getRatingLabel = (index: number) => {
  const labels = ["Very Bad", "Bad", "Better", "Good", "Excellent"]
  return labels[index]
}

// getItemIcon, getItemGradient, getItemBorder are not directly used for the template itself,
// but are kept for consistency if the template structure were to represent a specific type.
// For the template display, a generic FileText icon is used.
const getItemIcon = (item: IValue | ILeadership) => {
  if (isValue(item)) {
    return <Heart className="h-5 w-5 text-blue-600" />
  }
  return <Crown className="h-5 w-5 text-purple-600" />
}

const getItemGradient = (item: IValue | ILeadership) => {
  if (isValue(item)) {
    return "from-blue-50 to-indigo-50"
  }
  return "from-purple-50 to-violet-50"
}

const getItemBorder = (item: IValue | ILeadership) => {
  if (isValue(item)) {
    return "border-blue-200"
  }
  return "border-purple-200"
}


interface IProps {
  template: ITemplates
}

export const AdminQuestionnaireBox: React.FC<IProps> = ({ template }) => {
  const { api, store } = useAppContext()
  const [selectedItem, setSelectedItem] = useState<ITemplates>({
    ...defaultTemplate,
  })
  const [startValue, setStartValue] = useState("")
  const [stopValue, setStopValue] = useState("")
  const [continueValue, setContinueValue] = useState("")
  const [additionalComment, setAdditionalComment] = useState("")
  const [showNotification] = useState(false) // This state is not used for showing notifications in this component's logic
  const me = store.auth.meJson

  const onDeleteTemplate = async (template: ITemplates) => {
    try {
      await api.templates.delete(template)
    } catch (error) {
      // Handle error, e.g., ui.snackbar.load({ type: "error", message: "Failed to delete template." })
    }
  }

  const toggleEdit = (item: ITemplates) => {
    setSelectedItem(item)
    const template = store.templates.getItemById(item.id)?.asJson
    if (template) {
      store.templates.select(template)
      showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL)
    }
  }

  const toggleDelete = (item: ITemplates) => {
    setSelectedItem(item)
    if (item) { // Changed from selectedItem to item for correctness
      const template = store.templates.getItemById(item.id)?.asJson
      if (template) {
        if (window.confirm("Are you sure?")) {
          onDeleteTemplate(template)
        }
      }
    }
  }

  return (
    <div className="space-y-8">
      {template !== undefined && (
        <div
          className={`bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 shadow-lg overflow-hidden`}
        >
          {/* Item Header */}
          <div className="bg-white/60 backdrop-blur-sm p-6 border-b border-white/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">{template.title}</h3>
                <p className="text-sm text-slate-600">Template overview and statements</p>
              </div>
            </div>
          </div>
          {/* Statements */}
          <div className="p-6 space-y-6">
            {template.statements.map((statement, statementIndex) => (
              <div key={statementIndex} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm">
                <div className="mb-6">
                  <p className="text-lg font-medium text-slate-800 leading-relaxed">
                    {statement.statement}
                    <span className="text-red-500 ml-1">*</span>
                  </p>
                </div>
                {/* Rating Options - styled like user's, but non-interactive */}
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, ratingIndex) => {
                    const isChecked = false // Always false for admin view display
                    return (
                      <div
                        key={ratingIndex}
                        className={`relative group transition-all duration-300 cursor-default`}
                      >
                        <div
                          className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                            isChecked
                              ? `bg-gradient-to-br ${getRatingColor(ratingIndex)} border-transparent text-white shadow-lg`
                              : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md"
                          }`}
                        >
                          {/* Rating Number */}
                          <div className="text-center mb-2">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mx-auto ${
                                isChecked ? "bg-white/20" : "bg-slate-100"
                              }`}
                            >
                              {ratingIndex + 1}
                            </div>
                          </div>
                          {/* Rating Label */}
                          <div className="text-center">
                            <span className={`text-sm font-semibold ${isChecked ? "text-white" : "text-slate-700"}`}>
                              {getRatingLabel(ratingIndex)}
                            </span>
                          </div>
                          {/* Check Icon (only if checked, which is never here) */}
                          {isChecked && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 className="h-5 w-5 text-white" />
                            </div>
                          )}
                          {/* Hover Effect */}
                          <div
                            className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                              isChecked
                                ? "opacity-0"
                                : "opacity-0 group-hover:opacity-10 bg-gradient-to-br " + getRatingColor(ratingIndex)
                            }`}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {me?.role === "Admin" && (
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200/50 bg-white/60 backdrop-blur-sm">
              <button
                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                onClick={() => toggleEdit(template)}
                title="Edit Template"
              >
                <Edit className="h-5 w-5" />
              </button>
              <button
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                onClick={() => toggleDelete(template)}
                title="Delete Template"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Feedback Comments Preview Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <MessageSquare className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Feedback Comments Preview</h3>
            <p className="text-sm text-slate-600">These fields are for preview only and are not editable here.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Start Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Play className="h-4 w-4 text-green-600" />
              <label className="text-sm font-semibold text-slate-800">
                Start Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they start doing?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all duration-200 bg-slate-50 text-slate-700 cursor-not-allowed"
              value={startValue}
              maxLength={200}
              rows={4}
              placeholder="What should they start doing to improve performance?"
              onChange={(e) => setStartValue(e.target.value)}
              disabled
            ></textarea>
            <div className="text-xs text-slate-500 text-right">{startValue.length}/200</div>
          </div>
          {/* Stop Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Square className="h-4 w-4 text-red-600" />
              <label className="text-sm font-semibold text-slate-800">
                Stop Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they stop doing as they hinder their performance or the team's effectiveness?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all duration-200 bg-slate-50 text-slate-700 cursor-not-allowed"
              value={stopValue}
              maxLength={200}
              rows={4}
              placeholder="What should they stop doing that hinders performance?"
              onChange={(e) => setStopValue(e.target.value)}
              disabled
            ></textarea>
            <div className="text-xs text-slate-500 text-right">{stopValue.length}/200</div>
          </div>
          {/* Continue Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-blue-600" />
              <label className="text-sm font-semibold text-slate-800">
                Continue Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they continue doing that contributes positively?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 bg-slate-50 text-slate-700 cursor-not-allowed"
              value={continueValue}
              maxLength={200}
              rows={4}
              placeholder="What should they continue doing that contributes positively?"
              onChange={(e) => setContinueValue(e.target.value)}
              disabled
            ></textarea>
            <div className="text-xs text-slate-500 text-right">{continueValue.length}/200</div>
          </div>
        </div>
        {/* Additional Comments */}
        <div className="mt-6 space-y-3">
          <label className="text-sm font-semibold text-slate-800">Additional Comments (Optional)</label>
          <textarea
            className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200 bg-slate-50 text-slate-700 cursor-not-allowed"
            value={additionalComment}
            maxLength={500}
            rows={4}
            placeholder="Any additional feedback or context you'd like to provide..."
            onChange={(e) => setAdditionalComment(e.target.value)}
            disabled
          ></textarea>
          <div className="text-xs text-slate-500 text-right">{additionalComment.length}/500</div>
        </div>
      </div>
      {/* Notification (if needed, currently not triggered by admin logic) */}
      {showNotification && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm">
            Please fill in all required fields and select exactly one rating for each statement.
          </p>
        </div>
      )}
      {/* Modal */}
      {me?.role === "Admin" && (
        <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL}>
          <EditTemplateModal />
        </Modal>
      )}
    </div>
  )
}

export default AdminQuestionnaireBox
